/**
 * SHARE TRANSFER TO COMPANY
 * 
 * Component for transferring shares from a director's personal account
 * to the company account. Includes prominent warnings about the
 * 1,400,000 share lock rule.
 */

import React, { useState, useEffect } from 'react';
import { getSessionHeaders } from '../../lib/serverSession'
import type { CompanyAccount } from '../../types/company';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface ShareTransferToCompanyProps {
  company: CompanyAccount;
  userId: number;
  onTransferComplete: () => Promise<void>;
  onClose: () => void;
}

interface UserShareBalance {
  totalShares: number;
  availableShares: number;
  lockedShares: number;
}

export const ShareTransferToCompany: React.FC<ShareTransferToCompanyProps> = ({
  company,
  userId,
  onTransferComplete,
  onClose
}) => {
  const [loading, setLoading] = useState(true);
  const [transferring, setTransferring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userShares, setUserShares] = useState<UserShareBalance | null>(null);
  const [sharesToTransfer, setSharesToTransfer] = useState<number>(0);
  const [warningAccepted, setWarningAccepted] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');

  useEffect(() => {
    loadUserShares();
  }, [userId]);

  const loadUserShares = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${API_BASE_URL}/api/user/company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'load_user_shares',
          data: {}
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      const balance = result.data?.balance;
      setUserShares({
        totalShares: balance?.total_shares || 0,
        availableShares: balance?.available_shares || 0,
        lockedShares: balance?.locked_shares || 0
      });
    } catch (err) {
      console.error('Error loading user shares:', err);
      setError('Failed to load your share balance');
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async () => {
    if (!warningAccepted || confirmationText !== 'TRANSFER TO COMPANY') {
      setError('Please confirm you understand the transfer restrictions');
      return;
    }

    if (sharesToTransfer <= 0 || sharesToTransfer > (userShares?.availableShares || 0)) {
      setError('Invalid share amount');
      return;
    }

    setError(null);
    setTransferring(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/user/company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'transfer_shares_to_company',
          data: {
            companyId: company.id,
            shareAmount: sharesToTransfer,
            companyLegalName: company.legalName,
            userTotalShares: userShares?.totalShares || 0,
            userAvailableShares: userShares?.availableShares || 0
          }
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      await onTransferComplete();
      onClose();
    } catch (err: any) {
      console.error('Error transferring shares:', err);
      setError(err.message || 'Failed to transfer shares');
    } finally {
      setTransferring(false);
    }
  };

  const canTransfer = warningAccepted &&
                      confirmationText === 'TRANSFER TO COMPANY' &&
                      sharesToTransfer > 0 &&
                      sharesToTransfer <= (userShares?.availableShares || 0);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">🏢 Transfer Shares to Company</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">×</button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Critical Warning Banner */}
          <div className="bg-red-900/40 border-2 border-red-500 rounded-lg p-5">
            <div className="flex items-start gap-3">
              <span className="text-3xl">⚠️</span>
              <div>
                <h3 className="text-red-400 font-bold text-lg mb-2">IMPORTANT - READ CAREFULLY</h3>
                <p className="text-red-300 text-sm leading-relaxed">
                  Shares transferred to a company account are <strong>LOCKED</strong> and cannot be reversed
                  or transferred back to individual ownership until the company has sold all
                  <strong> 1,400,000 shares</strong> in total. This is a <strong>permanent, irreversible action</strong>.
                  Please confirm you understand this restriction before proceeding.
                </p>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-900/30 border border-red-500 rounded-lg p-4 text-red-400">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-center py-8 text-white">
              <div className="animate-spin text-3xl mb-3">⏳</div>
              <p>Loading your share balance...</p>
            </div>
          ) : (
            <>
              {/* Your Share Balance */}
              <div className="bg-gray-800 rounded-lg p-4">
                <h4 className="text-sm font-medium text-white mb-3">📊 Your Share Balance</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-gray-500 text-sm">Total Shares</p>
                    <p className="text-white text-xl font-bold">{userShares?.totalShares?.toLocaleString() || 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Available</p>
                    <p className="text-green-400 text-xl font-bold">{userShares?.availableShares?.toLocaleString() || 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Already Locked</p>
                    <p className="text-yellow-400 text-xl font-bold">{userShares?.lockedShares?.toLocaleString() || 0}</p>
                  </div>
                </div>
              </div>

              {/* Transfer To */}
              <div className="bg-gray-800 rounded-lg p-4">
                <h4 className="text-sm font-medium text-white mb-2">🏢 Transferring To</h4>
                <p className="text-white font-medium">{company.legalName}</p>
                <p className="text-gray-500 text-sm">{company.registrationNumber}</p>
              </div>

              {/* Share Amount */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">Shares to Transfer</label>
                <input
                  type="number"
                  value={sharesToTransfer || ''}
                  onChange={(e) => setSharesToTransfer(Math.max(0, parseInt(e.target.value) || 0))}
                  max={userShares?.availableShares || 0}
                  min={0}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white text-xl focus:border-yellow-400 focus:outline-none"
                  placeholder="Enter number of shares"
                />
                {sharesToTransfer > (userShares?.availableShares || 0) && (
                  <p className="text-red-400 text-sm mt-1">Cannot exceed available shares</p>
                )}
              </div>

              {/* Warning Acceptance */}
              <label className="flex items-start gap-3 cursor-pointer bg-gray-800 rounded-lg p-4">
                <input
                  type="checkbox"
                  checked={warningAccepted}
                  onChange={(e) => setWarningAccepted(e.target.checked)}
                  className="w-5 h-5 mt-0.5 rounded border-gray-600 text-yellow-500 focus:ring-yellow-500"
                />
                <span className="text-gray-300 text-sm">
                  I understand that shares transferred to the company will be <strong>LOCKED</strong> until
                  1,400,000 total shares are sold, and this action <strong>cannot be reversed</strong>.
                </span>
              </label>

              {/* Confirmation Text */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Type <span className="text-yellow-400 font-mono">TRANSFER TO COMPANY</span> to confirm
                </label>
                <input
                  type="text"
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value.toUpperCase())}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-yellow-400 focus:outline-none font-mono"
                  placeholder="TRANSFER TO COMPANY"
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-4 p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleTransfer}
            disabled={!canTransfer || transferring || loading}
            className="px-6 py-2 bg-red-600 hover:bg-red-500 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {transferring ? '⏳ Transferring...' : '🔒 Transfer & Lock Shares'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareTransferToCompany;

